import { FileUploadInput, FileUploadStatus } from '@/components/inputs';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { resetUploadStatus, updateEncrypted, updateFileData, updateUploadStatus } from '@/features/fileUploading/fileUploadingSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { useFileUploadContext } from '@/hooks/useFileUploadContext';
import { Alert, Input, SpinnerIcon } from '@/lib';
import { DataSource, DataSourceFileConfig, DataSourceType, DataSourceTypeCodeConfig } from '@/models/dataSource';
import { EStatus } from '@/models/fileUpload';
import { RootState, useAppDispatch, useAppSelector } from '@/reducers';
import { useCreateWorkspaceEncryptionKeyPairMutation, useGetWorkspaceEncryptionPublicKeyQuery } from '@/services/endpoints/workspaces';
import axios, { CancelTokenSource } from 'axios';
import { Form, Formik, FormikHelpers, FormikProps, getIn } from 'formik';
import { first, isEmpty } from 'lodash';
import * as csvParser from 'papaparse';
import { FunctionComponent, ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

export type FileDataSourceUploadFormModel = Partial<DataSource<DataSourceFileConfig>>;

interface NameAndFileDataSourceStepProps {
  dataSourceType: DataSourceType;
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onCancel?: VoidFunction;
}

const CSV_ROWS_NUMBER = 5;

export const NameAndFileDataSourceStep: FunctionComponent<NameAndFileDataSourceStepProps> = ({ dataSourceType, onNextStep, onCancel }) => {
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();
  const { t } = useTranslation();

  const dataSource = useAppSelector((state: RootState) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceFileConfig>> | undefined;

  const [axiosCancelToken, setAxiosCancelToken] = useState<CancelTokenSource | null>(null);
  const typeConfiguration = dataSourceType?.configuration as DataSourceTypeCodeConfig | undefined;
  const encrypted = typeConfiguration?.encrypted;

  const formRef = useRef<FormikProps<FileDataSourceUploadFormModel>>(null);

  const uploadStatus = useAppSelector((state) => state.fileUploading.status);
  const { uploadFile } = useFileUploadContext();
  const { isLoading, data: publicKey } = useGetWorkspaceEncryptionPublicKeyQuery({ workspaceId: workspace.id });
  const [createEncryptionPair, { isLoading: isCreatingPublicKey }] = useCreateWorkspaceEncryptionKeyPairMutation();

  let alertView: ReactNode;
  if (encrypted) {
    if (isLoading || isCreatingPublicKey) {
      alertView = (
        <span className='relative my-2 text-blue-500 opacity-75'>
          <SpinnerIcon className='mr-1 size-5' />
        </span>
      );
    } else if (publicKey?.keyId == null) {
      alertView = (
        <Alert
          type={'INFO'}
          title={t('data_source_edit:file.encryption.public_key_missing')}
          description={t('data_source_edit:file.encryption.key_pair_creation_message')}
          actionButtonText={t('data_source_edit:file.encryption.key_pair_creation_action')}
          onAction={() => createEncryptionPair({ workspaceId: workspace.id })}
        />
      );
    } else {
      alertView = (
        <Alert
          type={'INFO'}
          title={t('data_source_edit:file.encryption.public_key_title')}
          description={t('data_source_edit:file.encryption.public_key_message')}
          actionButtonText={t('data_source_edit:file.encryption.public_key_download')}
          onAction={() => {
            const file = new File([publicKey.publicKeyRing], `${workspace.id}-public-key.txt`, { type: 'text/plain' });
            const url = window.URL.createObjectURL(file);
            const a = window.document.createElement('a');
            a.download = file.name;
            a.href = url;
            a.click();
          }}
        />
      );
    }
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, t('data_source_edit:validations.too_short'))
      .max(50, t('data_source_edit:validations.too_long'))
      .required(t('data_source_edit:validations.required')),
    configuration: Yup.object({
      filename: Yup.string().required(t('data_source_edit:file.name_and_file.file_upload_input_component.input_file_required')),
    }),
  });

  // FormikHelpers

  const handleChangeFile = ({ setFieldValue }: Pick<FormikHelpers<Partial<DataSource<DataSourceFileConfig>>>, 'setFieldValue'>) => {
    setFieldValue('configuration.filename', undefined);

    dispatch(resetUploadStatus());
    dispatch(
      stashDataSource({
        configuration: {
          ...dataSource?.configuration,
          url: undefined,
          headers: undefined,
          headersFileKey: undefined,
        },
      })
    );
    setFieldValue('configuration.headers', undefined);
    setFieldValue('configuration.headersFileKey', undefined);
  };

  const handleCancelUploading = ({ setFieldValue }: Pick<FormikHelpers<Partial<DataSource<DataSourceFileConfig>>>, 'setFieldValue'>) => {
    setFieldValue('configuration.filename', undefined);

    axiosCancelToken?.cancel(t('axios:uploading_canceled'));
    dispatch(resetUploadStatus());
  };

  const handleUploadFile = (file: File, cancelToken: CancelTokenSource) => {
    uploadFile(file, cancelToken);
  };

  const handleValidFile = (results: csvParser.ParseResult<string[]>, file: File) => {
    const csvFile = results.data;

    if (!isEmpty(csvFile)) {
      dispatch(resetUploadStatus());
      dispatch(updateFileData({ preview: csvFile.slice(0, CSV_ROWS_NUMBER), header: csvFile[0], isDefaultHeader: true }));
      dispatch(
        stashDataSource({
          configuration: {
            ...dataSource?.configuration,
            headers: undefined,
            headersFileKey: undefined,
          },
        })
      );
    } else {
      throwFileError();
      return;
    }

    const cancelToken = axios.CancelToken.source();
    setAxiosCancelToken(cancelToken);

    if (file && cancelToken) {
      handleUploadFile(file, cancelToken);
    }
  };

  const throwFileError = () => {
    dispatch(
      updateUploadStatus({
        uploadingStatus: EStatus.ERROR,
        data: undefined,
        error: t('data_source_edit:file.name_and_file.file_upload_input_component.file_not_loaded'),
      })
    );
  };

  const handleNextStep = async () => {
    await formRef.current?.submitForm();

    if (formRef.current?.isValid && formRef.current?.values) {
      dispatch(stashDataSource({ ...formRef.current.values, type: dataSourceType }));

      onNextStep();
    }
  };

  return (
    <>
      {alertView}
      <Formik
        innerRef={formRef}
        initialValues={{ ...dataSource, name: '' }}
        onSubmit={() => {}}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}
      >
        {({ values, errors, handleChange, setFieldError, setFieldValue }) => {
          return (
            <Form className='flex max-w-3xl flex-col gap-16'>
              <div>
                <label className='block py-2 text-left text-sm font-medium text-gray-700'>{t('data_source_edit:file.name_data_source_input_label')}</label>
                <Input
                  name='name'
                  id='name'
                  type='text'
                  title={t('data_source_edit:file.name_and_file.source_name')}
                  value={values.name}
                  placeholder={t('data_source_edit:file.name_and_file.source_name_placeholder')}
                  onChange={(e) => {
                    setFieldError('name', undefined);
                    handleChange('name')(e.target.value);
                  }}
                  errorMsg={errors.name}
                />
              </div>

              <div>
                {uploadStatus && uploadStatus?.uploadingStatus !== EStatus.NOT_SELECTED ? (
                  <FileUploadStatus
                    handleChangeFile={() => handleChangeFile({ setFieldValue: setFieldValue })}
                    handleCancel={() => handleCancelUploading({ setFieldValue: setFieldValue })}
                    error={uploadStatus.error}
                    fileUpload={{
                      status: uploadStatus.uploadingStatus,
                      percentage: uploadStatus.progress || { total: 0, loaded: 0, percentageCompleted: 0 },
                    }}
                  />
                ) : (
                  <FileUploadInput
                    multiple={false}
                    name='configuration.filename'
                    id='configuration.filename'
                    error={getIn(errors.configuration, 'filename')}
                    onChange={(files) => {
                      const file = first(files);
                      if (file && file.name) {
                        setFieldValue('configuration.filename', file.name);
                        setFieldError('configuration.filename', undefined);
                      } else {
                        setFieldError('configuration.filename', 'data_source_edit:file.name_and_file.file_upload_input_component.file_not_loaded');
                        setFieldValue('configuration.filename', undefined);
                        return;
                      }
                      dispatch(updateEncrypted(encrypted));
                      if (!encrypted) {
                        // CSV VALIDATION
                        const csvPath = URL.createObjectURL(file);

                        csvParser.parse(csvPath, {
                          download: true,
                          skipEmptyLines: true,
                          complete: (results: csvParser.ParseResult<string[]>) => handleValidFile(results, file),
                          error: throwFileError,
                        });
                      } else {
                        const cancelToken = axios.CancelToken.source();
                        setAxiosCancelToken(cancelToken);

                        if (file && cancelToken) {
                          handleUploadFile(file, cancelToken);
                        }
                      }
                    }}
                  />
                )}
              </div>
            </Form>
          );
        }}
      </Formik>

      <StepPageActions
        primaryButton={{
          text: 'COMMON.confirm',
          onClick: handleNextStep,
          disabled: uploadStatus?.uploadingStatus === EStatus.ERROR,
        }}
        secondaryButton={{
          text: 'COMMON.cancel',
          onClick: onCancel,
        }}
      />
    </>
  );
};
